// ==========================================
// 1. TYPES GÉNÉRIQUES (MAGIE NOIRE)
// ==========================================

type Unpacked<T> = T extends (infer U)[] ? U : T;

// Génère les chemins d'accès profonds (ex: "user.profile.age")
export type Path<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? Unpacked<T[K]> extends object
          ? K | `${K}.${Path<Unpacked<T[K]>>}`
          : K
        : never;
    }[keyof T]
  : never;

// Extrait le type réel de la valeur au bout du chemin
type PathValue<T, P extends Path<T>> = P extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? Rest extends Path<Unpacked<T[Key]>>
      ? PathValue<Unpacked<T[Key]>, Rest>
      : never
    : never
  : P extends keyof T
  ? Unpacked<T[P]>
  : never;

/**
 * Map de comparateurs personnalisés.
 * DOIT retourner un nombre (style Array.sort) :
 * < 0 : actual < target
 * 0   : actual === target
 * > 0 : actual > target
 */
export type CustomComparatorMap<T> = {
  [P in Path<T>]?: (
    actualValue: PathValue<T, P>,
    targetValue: any // La valeur brute (string ou number) issue de la query
  ) => number;
};

// ==========================================
// 2. EXEMPLES DE SYNTAXE (DOCUMENTATION)
// ==========================================

/**
 * EXEMPLES DE SYNTAXE DE FILTRE
 * ------------------------------------------------------------------
 * Basé sur le type fictif suivant :
 * interface Order {
 *    id: string;
 *    total: number;
 *    isPaid: boolean;
 *    customer: { 
 *      name: string; 
 *      vip: boolean; 
 *      address: { 
 *        city: string 
 *      };
 *    };
 *    items: { 
 *      name: string; 
 *      price: number; 
 *      tags: string[] 
 *    }[];
 *    shippedAt: string | null;
 * }
 * ------------------------------------------------------------------
 *
 * 1. ÉGALITÉS & TYPES PRIMITIFS
 * "id = 'ORD-123'"                  -> Correspondance exacte de chaîne.
 * "total = 150.50"                  -> Correspondance de nombre (flottant ou entier).
 * "isPaid = true"                   -> Correspondance de booléen.
 * "shippedAt = null"                -> Vérification de valeur nulle/undefined.
 *
 * 2. OPÉRATEURS DE COMPARAISON
 * "total > 100"                     -> Strictement supérieur.
 * "total >= 100"                    -> Supérieur ou égal.
 * "total < 50"                      -> Strictement inférieur.
 * "total <= 50"                     -> Inférieur ou égal.
 * "total != 0"                      -> Différent de.
 *
 * 3. RECHERCHE DE TEXTE (CONTAINS)
 * "customer.name contains 'John'"   -> Vérifie présence sous-chaîne.
 *
 * 4. NÉGATION (NOT)
 * "NOT status = 'pending'"          -> Inverse une condition simple.
 * "NOT (age < 18 OR vip = false)"   -> Inverse un groupe.
 *
 * 5. ACCÈS IMBRIQUÉ (DOT NOTATION)
 * "customer.vip = true"             -> Accès à une propriété d'un sous-objet.
 * "customer.address.city = 'Paris'" -> Accès profond.
 *
 * 6. TABLEAUX : SOME (Au moins un)
 * "items.some(price > 1000)"        -> Vrai si au moins un article coûte > 1000.
 * "items.some(name = 'Laptop')"     -> Vrai si la commande contient un Laptop.
 *
 * 7. TABLEAUX : EVERY (Tous)
 * "items.every(price > 0)"          -> Vrai si TOUS les articles ont un prix positif.
 * "items.every(NOT name = 'virus')" -> Vrai si AUCUN article ne s'appelle 'virus'.
 *
 * 8. LOGIQUE BOOLEENNE (AND / OR)
 * "isPaid = true AND total > 500"   -> Les deux conditions vraies.
 * "total < 10 OR customer.vip = true" -> Au moins une condition vraie.
 *
 * 9. GROUPEMENT (PARENTHÈSES)
 * "isPaid = true AND (total > 100 OR vip = true)" -> Force la priorité.
 *
 * 10. IMBRICATION COMPLEXE
 * "items.some(tags.some(label = 'promo'))"
 */

// ==========================================
// 3. MOTEUR D'ÉVALUATION (CORE)
// ==========================================

export function matchEngine<T>(
  item: T,
  query: string,
  comparators: CustomComparatorMap<T> = {}
): boolean {
  let expression = query.trim();

  // 1. Gestion du OU (OR) - Prioritaire au découpage
  const orSplit = splitByTopLevel(expression, " OR ");
  if (orSplit.length > 1) {
    return orSplit.some((subExpr) => matchEngine(item, subExpr, comparators));
  }

  // 2. Gestion du ET (AND)
  const andSplit = splitByTopLevel(expression, " AND ");
  if (andSplit.length > 1) {
    return andSplit.every((subExpr) => matchEngine(item, subExpr, comparators));
  }

  // 3. Gestion du NOT (Négation) - NOUVEAU
  // On capture soit "NOT (expr)", soit "NOT expr"
  const notMatch = expression.match(/^NOT\s+(.+)$/i) || expression.match(/^NOT\((.+)\)$/i);
  if (notMatch) {
     const innerExpr = notMatch[1]; 
     // On évalue l'expression interne et on inverse le résultat
     return !matchEngine(item, innerExpr, comparators);
  }

  // 4. Gestion des Parenthèses englobantes "(...)"
  // Si l'expression est entourée de parenthèses (et qu'on a déjà splité les AND/OR externes)
  if (expression.startsWith("(") && expression.endsWith(")")) {
     return matchEngine(item, expression.slice(1, -1), comparators);
  }

  // 5. Gestion des Tableaux (some/every)
  // Regex : capture "field.method(condition)"
  const arrayMatch = expression.match(/^([\w\.]+)\.(some|every)\((.+)\)$/);
  if (arrayMatch) {
    const [, path, method, innerCondition] = arrayMatch;
    const arrayValue = getValue(item, path);
    
    if (!Array.isArray(arrayValue)) return false;

    if (method === "some") {
      return arrayValue.some((subItem) => matchEngine(subItem, innerCondition, comparators as any));
    } else {
      return arrayValue.every((subItem) => matchEngine(subItem, innerCondition, comparators as any));
    }
  }

  // 6. Comparaison Terme à Terme (Feuille de l'arbre)
  // Regex : capture "field operateur value"
  const opMatch = expression.match(/^([\w\.]+)\s*(>=|<=|!=|=|>|<|contains)\s*(.+)$/);
  if (opMatch) {
    const [, path, op, rawValue] = opMatch;
    const targetValue = parseValue(rawValue);
    const actualValue = getValue(item, path);

    // Détermination du delta (-1, 0, 1)
    let diff: number;
    const customComp = comparators[path as keyof typeof comparators] as any;

    if (customComp) {
      diff = customComp(actualValue, targetValue);
    } else {
      diff = defaultComparator(actualValue, targetValue);
    }

    return applyOperator(diff, op, actualValue, targetValue);
  }

  // Si rien ne matche, par défaut false (ou throw Error selon préférence)
  return false;
}

// ==========================================
// 4. HELPERS PRIVÉS
// ==========================================

const parseValue = (val: string): any => {
  val = val.trim();
  if (val === "true") return true;
  if (val === "false") return false;
  if (val === "null") return null;
  if (val === "undefined") return undefined;
  if (!isNaN(Number(val)) && val !== "") return Number(val);
  if ((val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"'))) {
    return val.slice(1, -1);
  }
  return val;
};

const getValue = (obj: any, path: string): any => {
  if (obj == null) return undefined;
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

const defaultComparator = (a: any, b: any): number => {
  if (a === b) return 0;
  if (a == null && b != null) return -1;
  if (a != null && b == null) return 1;
  return a < b ? -1 : 1;
};

const applyOperator = (diff: number, op: string, actual: any, target: any): boolean => {
  switch (op) {
    case "=": return diff === 0;
    case "!=": return diff !== 0;
    case ">": return diff > 0;
    case ">=": return diff >= 0;
    case "<": return diff < 0;
    case "<=": return diff <= 0;
    case "contains": 
      return String(actual).toLowerCase().includes(String(target).toLowerCase());
    default: return false;
  }
};

// Split intelligent qui ignore les séparateurs à l'intérieur des parenthèses
function splitByTopLevel(str: string, separator: string): string[] {
  let depth = 0;
  let lastIndex = 0;
  const result = [];
  
  for (let i = 0; i < str.length; i++) {
    if (str[i] === "(") depth++;
    else if (str[i] === ")") depth--;
    
    // Si on trouve le séparateur au niveau 0
    if (depth === 0 && str.substring(i, i + separator.length) === separator) {
      result.push(str.substring(lastIndex, i));
      i += separator.length - 1;
      lastIndex = i + 1;
    }
  }
  result.push(str.substring(lastIndex));
  return result;
}